<template>
  <b-container v-if="forbidden">
    <ForbiddenBanner/>
  </b-container>
  <b-container v-else>

    <b-row class="text-left">
      <b-col>
        <h2>Users and Teams</h2>
        <h5 class="text-secondary">
          <font-awesome-icon icon="sitemap" size="1x"/>
          {{ globalData.currentOrganization.name }}
        </h5>
      </b-col>
    </b-row>
    <b-row class="border shadow-lg user-select-none bg-light text-left mt-2 pt-2 pb-2" align-v="center">
      <b-col md="7" lg="5">
        <b-form-input
            autofocus
            v-model="filterString"
            placeholder="Search Users and Teams"
            autocomplete="off"
        />
      </b-col>
      <b-col cols="4">
        <b-form-checkbox class="text-right" v-model="hideUsersWithTeams" size="lg" switch>Hide Users with Teams
        </b-form-checkbox>
      </b-col>
    </b-row>
    <br/>
    <b-table
        hover
        small
        primary-key="uuid"
        :items="filteredUsers"
        :fields="fields"
        :filter="filterString"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-compare="compareTeams"
        :tbody-tr-class="rowClass"
    >
      <template #cell(name)="data">
        {{ data.item.name }}
        <b-badge v-if="isChanged(data.item.uuid)" variant="info" class="ml-1" title="Changed on this visit">updated</b-badge>
      </template>
      <template #cell(active)="data">
        <b-form-checkbox
            v-if="isRunner(data.item)"
            switch
            :checked="data.item.active"
            :aria-label="`Active: ${data.item.name}`"
            @change="onActiveChange(data.item, $event)"
        />
      </template>
      <template #cell(organizationRole)="data">
        <b-form-select
            size="sm"
            class="w-auto"
            :value="data.item.organizationRole"
            :options="organizationRoleOptions(data.item.organizationRole)"
            :disabled="data.item.organizationRole === 'Owner'"
            @change="onRoleChange(data.item.uuid, globalData.currentOrganization.uuid, $event)"
        />
      </template>
      <template #cell(teams)="data">
        <b-dropdown
            size="sm"
            :text="getUserDropDownText(data.item.teamCount)"
            class="m-0 w-50"
            :variant="getUserDropDownVariant(data.item)"
            boundary="window">
          <b-dropdown-form>
            <div v-for="team in teams" :key="team.uuid" class="d-flex align-items-center mb-1">
              <b-form-checkbox
                  size="sm"
                  :checked="isUserAssigned(data.item.groups, team.uuid)"
                  @change="onChange($event, data.item.uuid, team.uuid)"
              >
                {{ team.name }}
              </b-form-checkbox>
              <b-form-select
                  v-if="isUserAssigned(data.item.groups, team.uuid)"
                  size="sm"
                  class="w-auto ml-2"
                  :value="data.item.groups[team.uuid]"
                  :options="teamRoleOptions"
                  @change="onRoleChange(data.item.uuid, team.uuid, $event)"
              />
            </div>
          </b-dropdown-form>
        </b-dropdown>
      </template>
    </b-table>
  </b-container>
</template>

<script>

import ForbiddenBanner from "../components/ForbiddenBanner";
import ForbiddenContentMixin from "../components/ForbiddenContentMixin";

export default {
  name: 'UsersAndTeams',
  mixins: [ForbiddenContentMixin],
  components: {
    ForbiddenBanner,
  },
  data() {
    return {
      data: [],
      teams: [],
      hideUsersWithTeams: false,
      sortBy: 'teams',
      sortDesc: false,
      fields: [
        {key: "name", sortable: true, class: "text-truncate"},
        {key: "username", sortable: true, class: "text-truncate"},
        {key: "organizationRole", label: "Role", sortable: true},
        {key: "teams", sortable: true, class: "text-truncate"},
        // Only runners can be deactivated
        {key: "active", label: "Active", sortable: true},
      ],
      teamRoleOptions: [
        {value: 'Runner', text: 'Runner'},
        {value: 'Coach', text: 'Coach'},
      ],
      filterString: '',
      // User uuid -> the team count they had before their teams were first changed on this visit.
      // These users stay visible and in place (sorted by that count), so their teams can be changed again.
      changedUsers: {},
      // "user uuid:team uuid" -> the role of a team membership unchecked on this visit
      removedRoles: {},
    }
  },
  watch: {
    // A search result opened while already on this page
    q(value) {
      this.filterString = value;
    },
    filter(value) {
      this.hideUsersWithTeams = value === undefined ? false : value;
    },
    hideUsersWithTeams() {
      // Toggling the filter applies it to everyone again
      this.changedUsers = {};
    },
  },
  props: {
    q: String,
    filter: Boolean,
  },
  computed: {
    filteredUsers: function () {
      let self = this;
      if (!this.data) {
        return [];
      }
      // The filter shows the runners the "no team" alert counts: active ones without a team
      return this.data.filter(function (user) {
        return (user.teamCount === 0 && user.active) || !self.hideUsersWithTeams || self.isChanged(user.uuid);
      })
    }
  },
  created() {
    this.filterString = this.q;
    this.refresh();
  },
  mounted() {
    this.hideUsersWithTeams = this.filter === undefined ? false : this.filter;
    // console.error(this.filter);
  },
  methods: {
    compareTeams(userA, userB, key) {
      if (key === 'teams') {
        // Inactive runners after the active ones: having no team is only a problem for an active runner
        const inactiveA = this.sortInactive(userA);
        const inactiveB = this.sortInactive(userB);
        if (inactiveA !== inactiveB) {
          return inactiveA - inactiveB;
        }
        return Math.sign(this.sortTeamCount(userA) - this.sortTeamCount(userB));
      }
      return null;
    },
    sortInactive(user) {
      // Changed users keep their original position
      return this.isChanged(user.uuid) ? 0 : Number(!user.active);
    },
    sortTeamCount(user) {
      // Changed users keep their original position
      return this.isChanged(user.uuid) ? this.changedUsers[user.uuid] : user.teamCount;
    },
    isChanged(userUuid) {
      return userUuid in this.changedUsers;
    },
    refresh() {
      // This end point is for different purpose, however since the current user is Admin on the org,
      // we know it will return all the relevant teams
      this.$http.get(`/api/groups/teams`)
          .then(response => {
            this.teams = {};
            response.data.teams.forEach(item => {
              this.teams[item.group.uuid] = item.group;
            })
            this.$http.get(`/api/admin/users-and-groups`)
                .then(response => {
                  this.forbidden = false;
                  this.data = response.data
                  response.data.forEach(item => {
                    item.teamCount = Object.keys(item.groups).filter(value => Object.keys(this.teams).includes(value)).length;
                    item.organizationRole = item.groups[this.globalData.currentOrganization.uuid] || null;
                  })
                })
                .catch(e => {
                  this.HandleResponse(e);
                })
          })
          .catch(e => {
            this.HandleResponse(e);
          })
    },
    markChanged(userUuid) {
      if (!this.isChanged(userUuid)) {
        const user = this.data.find(item => item.uuid === userUuid);
        this.$set(this.changedUsers, userUuid, user ? user.teamCount : 0);
      }
    },
    isRunner(user) {
      // Disabling affects runner memberships only: Owners, Admins and Coaches can't be disabled
      return Object.values(user.groups).every(role => role === 'Runner');
    },
    rowClass(user) {
      return user && !user.active ? 'text-muted' : '';
    },
    onActiveChange(user, active) {
      // Stays in place, like a team change, so it can be undone
      this.markChanged(user.uuid);
      user.active = active;
      this.$http.post(`/api/groups/${this.globalData.currentOrganization.uuid}/users/${user.uuid}/activate`, {active: active})
          .catch(e => {
            console.warn(e);
          })
          .finally(() => {
            // Show the actual state
            this.refresh();
          })
    },
    onChange(value, userUuid, teamUuid) {
      this.markChanged(userUuid);
      // Unchecking removes the membership, so remember the role in case it's checked again on this visit
      const key = `${userUuid}:${teamUuid}`;
      if (!value) {
        const user = this.data.find(item => item.uuid === userUuid);
        this.removedRoles[key] = user ? user.groups[teamUuid] : undefined;
      }
      // Saved right away
      this.assign({
        teamUuid: teamUuid,
        userUuid: userUuid,
        value: value,
        role: (this.removedRoles[key] || 'Runner').toUpperCase(),
      })
    },
    onRoleChange(userUuid, groupUuid, role) {
      this.assign({
        teamUuid: groupUuid,
        userUuid: userUuid,
        value: true,
        role: role.toUpperCase(),
      })
    },
    assign(data) {
      this.$http.post(`/api/admin/assign`, data)
          .then(() => {
            this.refresh();
          })
          .catch(e => {
            console.warn(e);
            // Show the actual state
            this.refresh();
          })
    },
    organizationRoleOptions(currentRole) {
      const options = [
        {value: 'Runner', text: 'Runner'},
        {value: 'Coach', text: 'Coach'},
        {value: 'Admin', text: 'Admin'},
      ];
      if (currentRole === 'Owner') {
        options.push({value: 'Owner', text: 'Owner'});
      }
      if (currentRole === null) {
        options.unshift({value: null, text: '-', disabled: true});
      }
      return options;
    },
    isUserAssigned(userGroups, teamUuid) {
      return Object.keys(userGroups).includes(teamUuid);
    },
    getUserDropDownText(teamCount) {
      return teamCount === 0 ? "No Teams" : teamCount === 1 ? "1 Team" : teamCount + " Teams";
    },
    getUserDropDownVariant(user) {
      if (!user.active) {
        // No warning: an inactive runner doesn't need a team
        return "outline-secondary";
      }
      return user.teamCount === 0 ? "warning" : "outline-success";
    }
  }
}
</script>

<style scoped>

</style>
